import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AlertTriangle } from 'lucide-react';
import { UserDeleteProgress } from './UserDeleteProgress';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UserDeleteDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userName: string;
  userEmail?: string;
  onUserDeleted: () => void;
}

interface TableSelection {
  [key: string]: boolean;
}

export function UserDeleteDialog({
  isOpen,
  onOpenChange,
  userId,
  userName,
  userEmail,
  onUserDeleted,
}: UserDeleteDialogProps) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [selections, setSelections] = useState<TableSelection>({
    // Core User Data
    profiles: true,
    certificates: true,
    exam_attempts: true,
    exam_results: true,
    user_enrollments: true,
    
    // Financial Records
    payment_receipts: false, // Default unchecked for audit purposes
    payment_locks: true,
    
    // Content & Activity
    chat_logs: true,
    reviews: true,
    document_submissions: true,
    document_comments: true,
    document_access_logs: true,
    validation_attempts: true,
    review_modal_shown: true,
    
    // Auth account
    auth_account: true,
  });

  const [progress, setProgress] = useState<{
    current: number;
    total: number;
    currentTable: string;
    results: Array<{ table: string; success: boolean; message: string }>;
  }>({
    current: 0,
    total: 0,
    currentTable: '',
    results: [],
  });

  const handleSelectionChange = (table: string, checked: boolean) => {
    setSelections(prev => ({ ...prev, [table]: checked }));
  };

  const handleSelectAll = (category: string[]) => {
    setSelections(prev => {
      const newSelections = { ...prev };
      category.forEach(table => {
        newSelections[table] = true;
      });
      return newSelections;
    });
  };

  const handleDeselectAll = (category: string[]) => {
    setSelections(prev => {
      const newSelections = { ...prev };
      category.forEach(table => {
        if (table !== 'profiles') {
          newSelections[table] = false;
        }
      });
      return newSelections;
    });
  };

  const handleDelete = async () => {
    const selectedTables = Object.entries(selections)
      .filter(([_, selected]) => selected)
      .map(([table]) => table);

    if (!selections.profiles && selections.auth_account) {
      toast.error('Cannot delete auth account without deleting profile first');
      return;
    }

    setIsDeleting(true);
    const results: Array<{ table: string; success: boolean; message: string }> = [];
    
    setProgress({
      current: 0,
      total: selectedTables.length,
      currentTable: '',
      results: [],
    });

    // Deletion order to avoid foreign key violations
    const deletionOrder = [
      'exam_attempts',
      'exam_results',
      'certificates',
      'user_enrollments',
      'chat_logs',
      'validation_attempts',
      'review_modal_shown',
      'payment_locks',
      'document_comments',
      'document_access_logs',
      'document_submissions',
      'reviews',
      'payment_receipts',
      'profiles',
      'auth_account',
    ];

    let currentIndex = 0;

    for (const table of deletionOrder) {
      if (!selections[table]) continue;

      currentIndex++;
      setProgress(prev => ({
        ...prev,
        current: currentIndex,
        currentTable: table,
      }));

      try {
        if (table === 'auth_account') {
          const { data, error } = await supabase.functions.invoke('delete-user', {
            body: { userId }
          });
          if (error) throw error;
          if (data?.error) throw new Error(data.error);
          results.push({ table, success: true, message: 'Auth account deleted' });
        } else if (table === 'document_submissions') {
          const { error } = await supabase
            .from(table as any)
            .delete()
            .eq('submitted_by', userId);
          if (error) throw error;
          results.push({ table, success: true, message: `Deleted from ${table}` });
        } else if (table === 'document_comments') {
          const { error } = await supabase
            .from(table as any)
            .delete()
            .eq('created_by', userId);
          if (error) throw error;
          results.push({ table, success: true, message: `Deleted from ${table}` });
        } else if (table === 'document_access_logs') {
          const { error } = await supabase
            .from(table as any)
            .delete()
            .eq('accessed_by', userId);
          if (error) throw error;
          results.push({ table, success: true, message: `Deleted from ${table}` });
        } else {
          // For most tables, use user_id or id
          const idColumn = table === 'profiles' ? 'id' : 'user_id';
          const { error } = await supabase
            .from(table as any)
            .delete()
            .eq(idColumn, userId);
          if (error) throw error;
          results.push({ table, success: true, message: `Deleted from ${table}` });
        }
      } catch (error: any) {
        results.push({ 
          table, 
          success: false, 
          message: error.message || `Failed to delete from ${table}` 
        });
      }

      setProgress(prev => ({ ...prev, results: [...results] }));
    }

    setIsDeleting(false);

    const allSuccess = results.every(r => r.success);
    if (allSuccess) {
      toast.success('User deleted successfully', {
        description: `${userName} has been removed from ${results.length} table(s)`,
      });
      onUserDeleted();
      onOpenChange(false);
    } else {
      const failedCount = results.filter(r => !r.success).length;
      toast.error(`${failedCount} deletion(s) failed`, {
        description: 'Check the progress dialog for details',
      });
    }
  };

  const coreUserData: string[] = ['profiles', 'certificates', 'exam_attempts', 'exam_results', 'user_enrollments'];
  const financialRecords: string[] = ['payment_receipts', 'payment_locks'];
  const contentActivity: string[] = ['chat_logs', 'reviews', 'document_submissions', 'document_comments', 'document_access_logs', 'validation_attempts', 'review_modal_shown'];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Delete User: {userName}</DialogTitle>
          <DialogDescription>
            {userEmail && <span className="block">{userEmail}</span>}
          </DialogDescription>
        </DialogHeader>

        {isDeleting ? (
          <UserDeleteProgress
            current={progress.current}
            total={progress.total}
            currentTable={progress.currentTable}
            results={progress.results}
          />
        ) : (
          <div className="space-y-6">
            <Alert>
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>
                Warning: This action cannot be undone! Select which data to permanently delete.
              </AlertDescription>
            </Alert>

            {/* Core User Data */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">📊 Core User Data</h3>
                <div className="space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSelectAll(coreUserData)}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeselectAll(coreUserData)}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="space-y-2 pl-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="profiles"
                    checked={selections.profiles}
                    disabled={true}
                  />
                  <Label htmlFor="profiles" className="text-sm">
                    Profiles <span className="text-muted-foreground">(Required)</span>
                  </Label>
                </div>
                {['certificates', 'exam_attempts', 'exam_results', 'user_enrollments'].map(table => (
                  <div key={table} className="flex items-center space-x-2">
                    <Checkbox
                      id={table}
                      checked={selections[table]}
                      onCheckedChange={(checked) => handleSelectionChange(table, checked as boolean)}
                    />
                    <Label htmlFor={table} className="text-sm capitalize">
                      {table.replace(/_/g, ' ')}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Financial Records */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">💰 Financial Records</h3>
                <div className="space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSelectAll(financialRecords)}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeselectAll(financialRecords)}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="space-y-2 pl-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="payment_receipts"
                    checked={selections.payment_receipts}
                    onCheckedChange={(checked) => handleSelectionChange('payment_receipts', checked as boolean)}
                  />
                  <Label htmlFor="payment_receipts" className="text-sm">
                    Payment Receipts <span className="text-amber-600">⚠️ Keep for audit</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="payment_locks"
                    checked={selections.payment_locks}
                    onCheckedChange={(checked) => handleSelectionChange('payment_locks', checked as boolean)}
                  />
                  <Label htmlFor="payment_locks" className="text-sm">
                    Payment Locks
                  </Label>
                </div>
              </div>
            </div>

            {/* Content & Activity */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold">📝 Content & Activity</h3>
                <div className="space-x-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleSelectAll(contentActivity)}
                  >
                    Select All
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDeselectAll(contentActivity)}
                  >
                    Deselect All
                  </Button>
                </div>
              </div>
              <div className="space-y-2 pl-4">
                {contentActivity.map(table => (
                  <div key={table} className="flex items-center space-x-2">
                    <Checkbox
                      id={table}
                      checked={selections[table]}
                      onCheckedChange={(checked) => handleSelectionChange(table, checked as boolean)}
                    />
                    <Label htmlFor={table} className="text-sm capitalize">
                      {table.replace(/_/g, ' ')}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            {/* Auth Account */}
            <div className="space-y-2 pt-4 border-t">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="auth_account"
                  checked={selections.auth_account}
                  onCheckedChange={(checked) => handleSelectionChange('auth_account', checked as boolean)}
                />
                <Label htmlFor="auth_account" className="text-sm font-semibold">
                  Also delete authentication account <span className="text-muted-foreground">(Recommended)</span>
                </Label>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button variant="destructive" onClick={handleDelete}>
                Delete Selected Data
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
